package com.swetdot.swetgram.data

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.swetdot.swetgram.cache.SwetgramCache
import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.cache.toCacheJson
import com.swetdot.swetgram.data.cache.toCacheRow
import com.swetdot.swetgram.data.cache.toDb
import com.swetdot.swetgram.data.cache.toDomain
import com.swetdot.swetgram.data.cache.toWaveformCacheJson
import com.swetdot.swetgram.domain.Chat
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.domain.Message
import com.swetdot.swetgram.domain.MessageId
import com.swetdot.swetgram.domain.MessageKind
import com.swetdot.swetgram.domain.MessageState
import com.swetdot.swetgram.domain.User
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.protocol.ProtocolJson
import com.swetdot.swetgram.protocol.dto.EditMessageRequest
import com.swetdot.swetgram.protocol.dto.ForwardMessageRequest
import com.swetdot.swetgram.protocol.dto.MarkReadRequest
import com.swetdot.swetgram.protocol.dto.MessageChange
import com.swetdot.swetgram.protocol.dto.MessageChangePage
import com.swetdot.swetgram.protocol.dto.MessagePage
import com.swetdot.swetgram.protocol.dto.ReactToMessageRequest
import com.swetdot.swetgram.protocol.dto.SendMessageRequest
import com.swetdot.swetgram.protocol.dto.TypingRequest
import com.swetdot.swetgram.protocol.dto.Message as MessageDto
import com.swetdot.swetgram.protocol.dto.MessageType as MessageTypeDto
import io.ktor.client.request.parameter
import io.ktor.client.request.setBody
import io.ktor.http.HttpMethod
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.withContext
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.jsonObject
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * Messages for one conversation, cache-first like the chat list.
 *
 * The timeline is served from SQLite so opening a chat is instant and scrolling never blocks on
 * a request. [refresh] merges the newest page in behind it.
 */
class MessageRepository(
    private val api: ApiClient,
    private val cache: SwetgramCache,
) {
    private val queries = cache.cacheQueries

    /** Newest first, matching the reversed list the chat screen renders into. */
    fun observeMessages(chatId: ChatId, limit: Long = TIMELINE_WINDOW): Flow<List<Message>> =
        queries.selectTimeline(chatId.value, limit)
            .asFlow()
            .mapToList(ioDispatcher)
            .map { rows ->
                rows.mapNotNull { row ->
                    // A message whose sender is not cached cannot be rendered without inventing
                    // a name, so it is skipped rather than shown blank. The next refresh brings
                    // the user in and it appears.
                    queries.selectUser(row.senderId).executeAsOneOrNull()
                        ?.toDomain()
                        ?.let { row.toDomain(it) }
                }
            }

    fun observeChat(chatId: ChatId): Flow<Chat?> =
        queries.selectChat(chatId.value)
            .asFlow()
            .mapToList(ioDispatcher)
            .map { rows ->
                rows.firstOrNull()?.let { row ->
                    val members = queries.selectMembers(row.id).executeAsList().map { it.toDomain() }
                    row.toDomain(members = members, lastMessage = null)
                }
            }

    /**
     * The newest page. Returns the cursor for the page BEFORE it, or null at the start of history.
     *
     * The cursor is returned rather than stored because where the reader has got to is a property
     * of the open screen, not of the cache -- see `ChatViewModel.loadOlder`, which keeps it and is
     * careful not to let a background refresh reset it.
     */
    suspend fun refresh(chatId: ChatId): String? = fetchPage(chatId, cursor = null)

    /**
     * One page of OLDER history, from [cursor]. Returns the cursor beyond it, or null at the end.
     *
     * `listMessages` declares `Cursor` and its own summary says "cursor loads older messages"
     * (openapi.yaml:1179/1184); nothing else about scroll-back is contract or spec. See
     * DECISIONS §22 -- this is a decision, not a requirement anybody wrote down.
     */
    suspend fun loadOlder(chatId: ChatId, cursor: String): String? = fetchPage(chatId, cursor)

    /**
     * Whether the cache already holds anything sent before [before] in this conversation.
     *
     * What makes scroll-back cache-first: a reader paging back through history the app has
     * already seen must not wait on -- or pay for -- a request. Only when this answers false does
     * [loadOlder] go to the network.
     */
    suspend fun hasCachedOlder(chatId: ChatId, before: Instant): Boolean =
        withContext(ioDispatcher) {
            queries.selectTimelineBefore(chatId.value, before.toEpochMilliseconds(), 1)
                .executeAsOneOrNull() != null
        }

    private suspend fun fetchPage(chatId: ChatId, cursor: String?): String? {
        val page: MessagePage = api.get("conversations/${chatId.value}/messages") {
            parameter("limit", PAGE_SIZE)
            // Ktor drops a null parameter, so the first page sends no cursor at all.
            parameter("cursor", cursor)
        }

        withContext(ioDispatcher) {
            queries.transaction {
                page.items.forEach { dto -> cacheMessage(dto) }
            }
        }
        return page.pageInfo.nextCursor?.takeIf { page.pageInfo.hasMore }
    }

    /**
     * Writes one server message into the cache. MUST be called inside a [queries] transaction.
     *
     * The sender goes in FIRST: the timeline query drops messages whose author is unknown, so
     * writing the message before its author would hide it until the next refresh.
     */
    private fun cacheMessage(dto: MessageDto) {
        val message = dto.toDomain()
        val senderRow = message.sender.toCacheRow()
        queries.upsertUser(
            id = senderRow.id,
            username = senderRow.username,
            displayName = senderRow.displayName,
            avatarUrl = senderRow.avatarUrl,
            bio = senderRow.bio,
            location = senderRow.location,
            dateOfBirth = senderRow.dateOfBirth,
            showBirthYear = senderRow.showBirthYear,
            isOnline = senderRow.isOnline,
            lastSeenAt = senderRow.lastSeenAt,
            isDeleted = senderRow.isDeleted,
            hasBlackBadge = senderRow.hasBlackBadge,
        )

        queries.upsertMessage(
            chatId = message.chatId.value,
            clientId = message.clientId,
            serverId = message.id.value,
            senderId = message.sender.id.value,
            kind = message.kind.toDb(),
            text = message.text,
            imageUrl = message.imageUrl,
            voiceUrl = message.voiceUrl,
            voiceDurationMs = message.voiceDurationMs,
            voiceWaveformJson = message.voiceWaveform.toWaveformCacheJson(),
            replyToId = message.replyTo?.id?.value,
            replyToSenderId = message.replyTo?.senderId?.value,
            replyToSenderDisplayName = message.replyTo?.senderDisplayName,
            replyToText = message.replyTo?.text,
            replyToKind = message.replyTo?.kind?.toDb(),
            state = message.state.toDb(),
            sentAt = message.sentAt.toEpochMilliseconds(),
            editedAt = message.editedAt?.toEpochMilliseconds(),
            isDeleted = message.isDeleted.toDb(),
            version = message.version.toLong(),
            canEdit = message.canEdit.toDb(),
            canDeleteForEveryone = message.canDeleteForEveryone.toDb(),
            reactionsJson = message.reactions.toCacheJson(),
            picsoId = message.picso?.id,
            picsoExpiresAt = message.picso?.expiresAt,
            // The upsert COALESCEs this, so a server copy of a message we sent ourselves never
            // erases the key its retry would need.
            idempotencyKey = null,
            retryCount = 0,
        )
    }

    // -------------------------------------------------------------------------- reconciliation
    //
    // `listHistory` EXCLUDES deleted rows (`AND m.deleted_at IS NULL`, server MessageRepository
    // :340 and :358) and the client only upserts rows a response contained -- so a `refresh` can
    // never learn that a message it already cached is gone. Without the feed below, a message
    // deleted for everyone stays rendered on the recipient's device permanently: the cache is the
    // UI's source of truth, so backgrounding, reconnecting and restarting all leave it there.
    //
    // STA §31.5's stated mitigation for realtime complexity is "use client reconciliation from
    // REST history; do not depend only on WebSocket delivery", and DECISIONS §11 already records
    // that this client reconciles edits and tombstones over REST. This is the half of that which
    // was built on both sides and called by nobody. See DECISIONS §22.

    /**
     * Applies every change the server has recorded for this conversation since the stored cursor:
     * edits through the ordinary upsert, deletions through `applyTombstone`. `listMessageChanges`,
     * `200`.
     *
     * THE CURSOR IS PERSISTED IN THE SAME TRANSACTION AS THE CHANGES IT COVERS. Advancing it
     * separately would, on any failure between the two writes, skip those changes for good -- and
     * the whole point of the feed is that a missed deletion is unrecoverable.
     *
     * BOOTSTRAP. With no stored cursor the newest cached message anchors the walk through
     * `after_message_id`, which is what the contract says that parameter is for
     * (openapi.yaml:808-812). With neither -- a conversation the cache has never seen -- nothing
     * is sent and the server starts from the beginning of what is still visible to this member.
     *
     * BOUNDED. At most [MAX_RECONCILE_PAGES] pages per call: a conversation with a long change
     * history must not turn opening it into an unbounded walk. The cursor is stored as each page
     * lands, so the next call resumes where this one stopped.
     *
     * Returns how many changes were applied.
     */
    suspend fun reconcile(chatId: ChatId, maxPages: Int = MAX_RECONCILE_PAGES): Int {
        var cursor = withContext(ioDispatcher) {
            queries.selectChat(chatId.value).executeAsOneOrNull()?.changeCursor
        }
        val anchor = if (cursor != null) {
            null
        } else {
            withContext(ioDispatcher) {
                queries.selectLastMessage(chatId.value).executeAsOneOrNull()?.serverId
            }
        }

        var applied = 0
        repeat(maxPages) { pageIndex ->
            val page: MessageChangePage = api.get("conversations/${chatId.value}/messages/changes") {
                parameter("limit", PAGE_SIZE)
                parameter("change_cursor", cursor)
                // Only ever on the very first request of a bootstrap; once a cursor exists it
                // wins on the server anyway ("change_cursor wins when both are supplied").
                parameter("after_message_id", anchor.takeIf { pageIndex == 0 && cursor == null })
            }

            val plan = planReconcile(page, chatId)
            applyReconcilePlan(chatId, plan)
            applied += plan.updated.size + plan.deleted.size

            cursor = plan.changeCursor
            if (!plan.hasMore) return applied
        }
        return applied
    }

    /** [planReconcile]'s decisions, written down. One transaction, changes and cursor together. */
    private suspend fun applyReconcilePlan(chatId: ChatId, plan: ReconcilePlan) {
        withContext(ioDispatcher) {
            queries.transaction {
                plan.updated.forEach { dto -> cacheMessage(dto) }
                plan.deleted.forEach { serverId -> queries.applyTombstone(serverId) }
                queries.setChangeCursor(plan.changeCursor, chatId.value)
            }
        }
    }

    /**
     * Optimistic send.
     *
     * The row is written as SENDING and rendered BEFORE the request leaves, which is what
     * DECISIONS §5 means by "message sending uses optimistic UI". The client id doubles as the
     * row's local identity, so when the acknowledgement arrives the same row is updated rather
     * than replaced -- the bubble never disappears and reappears, and the list key stays stable.
     *
     * The idempotency key is generated ONCE and stored with the row, because §5 also requires a
     * retry to reuse the same client_id and Idempotency-Key while the content is unchanged. A
     * key held only in memory would be lost on restart and the retry would post a duplicate.
     */
    suspend fun send(chatId: ChatId, senderId: UserId, text: String, replyingTo: Message? = null): String {
        val clientId = newUuid()
        val idempotencyKey = newUuid()
        val now = Clock.System.now()

        withContext(ioDispatcher) {
            queries.upsertMessage(
                chatId = chatId.value,
                clientId = clientId,
                serverId = null,
                senderId = senderId.value,
                kind = MessageKind.TEXT.toDb(),
                text = text,
                imageUrl = null,
                voiceUrl = null,
                voiceDurationMs = null,
                voiceWaveformJson = null,
                replyToId = replyingTo?.id?.value,
                replyToSenderId = replyingTo?.sender?.id?.value,
                replyToSenderDisplayName = replyingTo?.sender?.displayName,
                replyToText = replyingTo?.text,
                replyToKind = replyingTo?.kind?.toDb(),
                state = MessageState.SENDING.toDb(),
                sentAt = now.toEpochMilliseconds(),
                editedAt = null,
                isDeleted = false.toDb(),
                version = 1,
                canEdit = false.toDb(),
                canDeleteForEveryone = false.toDb(),
                reactionsJson = null,
                picsoId = null,
                picsoExpiresAt = null,
                idempotencyKey = idempotencyKey,
                retryCount = 0,
            )
        }

        deliver(chatId, clientId, idempotencyKey, text, mediaId = null, replyToId = replyingTo?.id?.value)
        return clientId
    }

    /**
     * Sends an already-uploaded image.
     *
     * NOT optimistic, unlike [send], and that is a deliberate difference rather than an omission.
     * An optimistic row would have to survive a restart to be retryable, which means persisting
     * the `media_id` -- a column the cache does not have. Writing the row without it would
     * produce a pending message that [retryPending] could only re-send as an EMPTY TEXT message,
     * silently turning the user's photo into a blank bubble.
     *
     * The user is not left staring at nothing meanwhile: the upload is the slow part and it has
     * its own progress, so the message appears when the send lands, a second or so after the bar
     * fills. A failure throws and nothing is cached, so there is no half-sent row to reconcile.
     */
    suspend fun sendImage(chatId: ChatId, mediaId: String, replyingTo: Message? = null): String {
        val clientId = newUuid()
        deliver(
            chatId,
            clientId,
            idempotencyKey = newUuid(),
            text = null,
            mediaId = mediaId,
            replyToId = replyingTo?.id?.value,
            // THERE IS NO ROW TO MARK. `markFailed` would update zero rows, which is harmless,
            // but running the FAILED bookkeeping for a send that wrote nothing is what made the
            // two paths look the same when they are not: an image send that threw leaves no
            // trace, so there is nothing for a retry to find and nothing for the user to act on.
            // The failure is reported by the throw and by `ChatViewModel.attachImage` alone.
            optimisticRow = false,
        )
        return clientId
    }

    suspend fun sendVoice(
        chatId: ChatId,
        mediaId: String,
        replyingTo: Message? = null,
        clientId: String = newUuid(),
        idempotencyKey: String = newUuid(),
    ): String {
        deliver(
            chatId = chatId,
            clientId = clientId,
            idempotencyKey = idempotencyKey,
            text = null,
            mediaId = mediaId,
            messageType = MessageTypeDto.VOICE,
            replyToId = replyingTo?.id?.value,
            optimisticRow = false,
        )
        return clientId
    }

    /**
     * Re-sends everything still pending, reusing each row's original identifiers so the server
     * collapses duplicates rather than creating them.
     *
     * CALLED WHEN A CONVERSATION IS OPENED -- `ChatViewModel.init` -- and not at app start. The
     * doc comment here used to claim the latter, which was simply false; the honest place for an
     * app-start pass is `AppContainer`, which this change does not own. Recorded in DECISIONS §22.
     *
     * ONE POISON ROW MUST NOT END THE PASS. `selectPending` has no chat filter and is ordered
     * oldest-first, so before the guard below the first row the server permanently refused -- a
     * 403 after a block, a 422, a send into a conversation the user was removed from -- threw out
     * of the loop and blocked every later pending message in EVERY conversation, forever.
     *
     * Returns what happened, which is what makes the rule assertable without a database: see
     * [deliverPendingPass].
     */
    suspend fun retryPending(): PendingPassReport {
        val pending = withContext(ioDispatcher) {
            queries.selectPending().executeAsList().map { row ->
                PendingSend(
                    chatId = row.chatId,
                    clientId = row.clientId,
                    idempotencyKey = row.idempotencyKey,
                    // Text only, by construction: `sendImage` never writes a pending row,
                    // precisely because the media id it would need to retry is not persisted.
                    text = row.text,
                    replyToId = row.replyToId,
                    retryCount = row.retryCount,
                )
            }
        }
        return deliverPendingPass(pending) { row ->
            deliver(
                chatId = ChatId(row.chatId),
                clientId = row.clientId,
                idempotencyKey = requireNotNull(row.idempotencyKey),
                text = row.text.orEmpty(),
                mediaId = null,
                replyToId = row.replyToId,
            )
        }
    }

    /**
     * Re-sends ONE message the user asked to send again, whatever its retry count.
     *
     * The bound in [deliverPendingPass] governs the AUTOMATIC pass only. A row that has stopped
     * being retried for free is exactly the row this exists for -- the user is standing there,
     * they pressed the thing, and refusing them because a background pass gave up would be the
     * worse of the two failures.
     *
     * Reuses the row's stored `clientId` and `idempotencyKey`, which is the whole reason the key
     * is a column rather than a field: DECISIONS §5 requires a retry to reuse both while the
     * content is unchanged, and a key held in memory would be gone by the time the user retried.
     */
    suspend fun retry(chatId: ChatId, clientId: String) {
        val row = withContext(ioDispatcher) {
            queries.selectMessageByClientId(chatId.value, clientId).executeAsOneOrNull()
        } ?: return

        // A locally composed row always has one -- `send` writes it in the same statement as the
        // row -- so this is the unreachable case, not the ordinary one. Minting rather than
        // refusing, because a row with no key has by definition never been sent, and a message
        // the user is looking at is worth more than the theoretical duplicate.
        val key = row.idempotencyKey ?: newUuid()

        withContext(ioDispatcher) {
            queries.markState(MessageState.SENDING.toDb(), chatId.value, clientId)
        }
        deliver(chatId, clientId, key, row.text.orEmpty(), mediaId = null, replyToId = row.replyToId)
    }

    /**
     * Forgets a message that never reached the server.
     *
     * Only ever right for a FAILED own row: nothing on the server knows about it, so there is
     * nothing to reconcile against and the row would otherwise sit in the outbox forever, costing
     * a request on every chat open until its retry bound expired. A message the server HAS seen
     * goes through [delete] instead -- deleting that one locally would leave it to be resurrected
     * by the next refresh.
     */
    suspend fun discard(chatId: ChatId, clientId: String) {
        withContext(ioDispatcher) { queries.deleteMessage(chatId.value, clientId) }
    }

    /**
     * `deleteMessage`, `204`. SDS §12.5 and SPS §10.13 both put Delete in the long-press menu.
     *
     * `scope` is a REQUIRED query parameter with no default (openapi.yaml:1299-1306), and the two
     * values are not interchangeable: `everyone` is refused with `403 message_delete_not_allowed`
     * for a message the caller did not send and with `409 delete_window_expired` past 48 hours,
     * while `self` is available to any member. `Message.can_delete_for_everyone` is the server's
     * own answer to which one may be offered, and it is carried on the cached row.
     *
     * The local row is TOMBSTONED rather than removed, for both scopes. It disappears from the
     * timeline either way -- `selectTimeline` filters `isDeleted = 0` -- but a tombstone survives
     * the next reconciliation, where a deleted row would simply be written back by it.
     */
    suspend fun delete(messageId: MessageId, scope: MessageDeleteScope) {
        api.delete<Unit>("messages/${messageId.value}") {
            parameter("scope", scope.wire)
        }
        withContext(ioDispatcher) { queries.applyTombstone(messageId.value) }
    }

    /** Updates an acknowledged text message and replaces its cached row with the server copy. */
    suspend fun edit(messageId: MessageId, content: String, version: Int) {
        val dto: MessageDto = api.patch("messages/${messageId.value}") {
            setBody(EditMessageRequest(content = content, version = version))
        }
        withContext(ioDispatcher) {
            queries.transaction { cacheMessage(dto) }
        }
    }

    /** Forwards messages in the order supplied and caches each server-created destination row. */
    suspend fun forward(messageIds: List<MessageId>, targetChatId: ChatId) {
        messageIds.forEach { sourceId ->
            val clientId = newUuid()
            val dto: MessageDto = api.post(
                path = "messages/${sourceId.value}/forward",
                idempotencyKey = newUuid(),
            ) {
                setBody(
                    ForwardMessageRequest(
                        conversationId = targetChatId.value,
                        clientId = clientId,
                    ),
                )
            }
            withContext(ioDispatcher) {
                queries.transaction { cacheMessage(dto) }
            }
        }
    }

    private suspend fun deliver(
        chatId: ChatId,
        clientId: String,
        idempotencyKey: String,
        text: String?,
        mediaId: String?,
        messageType: MessageTypeDto = if (mediaId != null) MessageTypeDto.IMAGE else MessageTypeDto.TEXT,
        replyToId: String? = null,
        optimisticRow: Boolean = true,
    ) {
        try {
            val dto: com.swetdot.swetgram.protocol.dto.Message = api.post(
                path = "conversations/${chatId.value}/messages",
                idempotencyKey = idempotencyKey,
            ) {
                setBody(
                    SendMessageRequest(
                        clientId = clientId,
                        type = messageType,
                        content = text,
                        mediaId = mediaId,
                        replyToMessageId = replyToId,
                    ),
                )
            }
            val message = dto.toDomain()
            withContext(ioDispatcher) {
                queries.upsertMessage(
                    chatId = message.chatId.value,
                    clientId = message.clientId,
                    serverId = message.id.value,
                    senderId = message.sender.id.value,
                    kind = message.kind.toDb(),
                    text = message.text,
                    imageUrl = message.imageUrl,
                    voiceUrl = message.voiceUrl,
                    voiceDurationMs = message.voiceDurationMs,
                    voiceWaveformJson = message.voiceWaveform.toWaveformCacheJson(),
                    replyToId = message.replyTo?.id?.value,
                    replyToSenderId = message.replyTo?.senderId?.value,
                    replyToSenderDisplayName = message.replyTo?.senderDisplayName,
                    replyToText = message.replyTo?.text,
                    replyToKind = message.replyTo?.kind?.toDb(),
                    // The server's own createdAt replaces the optimistic one, so the bubble
                    // settles into the same position every other client sees it in.
                    state = message.state.toDb(),
                    sentAt = message.sentAt.toEpochMilliseconds(),
                    editedAt = message.editedAt?.toEpochMilliseconds(),
                    isDeleted = message.isDeleted.toDb(),
                    version = message.version.toLong(),
                    canEdit = message.canEdit.toDb(),
                    canDeleteForEveryone = message.canDeleteForEveryone.toDb(),
                    reactionsJson = message.reactions.toCacheJson(),
                    picsoId = message.picso?.id,
                    picsoExpiresAt = message.picso?.expiresAt,
                    idempotencyKey = idempotencyKey,
                    retryCount = 0,
                )
            }
        } catch (cancellation: CancellationException) {
            // Not a failure: the screen was closed, or a newer pass replaced this one. Marking
            // the row FAILED here would show a cross on a message that is still perfectly
            // sendable, and would burn one of its automatic retries doing it.
            throw cancellation
        } catch (e: Throwable) {
            // The row stays, marked FAILED, so the message is visible with a retry affordance
            // instead of vanishing. Deleting it would lose what the user typed.
            //
            // Only when there IS a row. `sendImage` deliberately writes none, and running this
            // for it updated nothing while claiming the send had failed -- see [sendImage].
            if (optimisticRow) {
                withContext(ioDispatcher) { queries.markFailed(chatId.value, clientId) }
            }
            throw e
        }
    }

    /**
     * Marks everything up to [messageId] read. 204 on success.
     *
     * CUMULATIVE, which is the property every caller has to know: the server clears the unread count
     * up to this message, so naming the newest one marks the whole run behind it. That makes the
     * choice of id a decision about what the reader has actually seen rather than a detail, and it
     * is made in one place -- `ChatViewModel.observeReading`, via `readReceiptTarget`. It is not a
     * per-message receipt: calling it once per row would be both wrong and thirty requests.
     *
     * Nothing is written to the cache. The unread count lives on the conversation row, which the
     * chat list re-reads when it comes back into view (see `ui/Navigation.kt`) -- the server does not
     * announce a read on the conversation channel, because telling the other party when you read
     * them is a presence disclosure of its own.
     */
    suspend fun markRead(chatId: ChatId, messageId: String) {
        api.post<Unit>("conversations/${chatId.value}/read") {
            setBody(MarkReadRequest(lastReadMessageId = messageId))
        }
    }

    /**
     * Announces that the signed-in user has started or stopped typing. 204.
     *
     * No cache write, because there is nothing durable here -- the server publishes the frame to
     * the conversation channel and forgets it. The caller is responsible for not sending one of
     * these per keystroke; see `ChatViewModel.onDraftChanged`.
     */
    suspend fun setTyping(chatId: ChatId, isTyping: Boolean) {
        api.post<Unit>("conversations/${chatId.value}/typing") {
            setBody(TypingRequest(isTyping = isTyping))
        }
    }

    // -------------------------------------------------------------------------- pinned messages
    //
    // SPS §10.13 and SDS §12.5 both put "Pin" in the message long-press menu, and all three
    // operations have been implemented, tested and deployed on the server since the messaging work
    // landed -- with nothing in the app calling any of them. Verified against the deployed server
    // on 2026-08-06: PUT and DELETE are both idempotent (a repeat answers 204), a sixth pin answers
    // 409 `pin_limit_reached`, and a message id that is not in the conversation answers 404.

    /**
     * The conversation's pinned messages, newest pin first. `listPinnedMessages`, `200`.
     *
     * NOT CACHED, unlike the timeline. The cache has no table for a pin and adding one is a schema
     * change this work does not own; more to the point a pin is per-CONVERSATION rather than
     * per-message state, so it has nowhere to live on the `messages` row either. The list is small
     * and capped, so it is fetched on open and re-fetched when the socket says a pin changed.
     *
     * Decoded by hand rather than through a response DTO, for the reason `ChatCommands.deleteForMe`
     * records for its request body: the contract's `{"items": [...]}` shape has no type in
     * `:protocol`, and declaring one in `:shared` would put a wire type on the wrong side of
     * IMPLEMENTATION_NOTES §10. `MessagePage` is the wrong shape -- it requires `page_info`, which
     * this endpoint does not send, so it fails to decode rather than merely over-reading.
     *
     * TODO(protocol): a `PinnedMessages` DTO belongs in `:protocol` next to `ConversationPage`,
     * which would delete this function's body down to one `api.get`.
     */
    suspend fun pinnedMessages(chatId: ChatId): List<Message> {
        val raw = api.executeRaw(
            method = HttpMethod.Get,
            path = "conversations/${chatId.value}/pinned-messages",
            idempotencyKey = null,
        ) {}
        val items = ProtocolJson.parseToJsonElement(raw).jsonObject["items"] ?: return emptyList()
        return ProtocolJson
            .decodeFromJsonElement(ListSerializer(MessageDto.serializer()), items)
            .map { it.toDomain() }
    }

    /**
     * Pins one message. `pinMessage`, `204`, `409 pin_limit_reached` past the cap.
     *
     * The cap is FIVE PER CONVERSATION -- `maxItems: 5` on `listPinnedMessages`, DECISIONS §5
     * ("five messages per conversation"), and `MessageService.MAX_PINNED_MESSAGES`. It is a
     * different limit from the five PINNED CONVERSATIONS of `ChatListViewModel.MAX_PINNED`, and the
     * two share the `pin_limit_reached` code, so the message the user is shown has to be chosen by
     * the caller rather than taken from the server.
     *
     * PUT, so it goes through `execute` directly; see `ChatRepository.mute` for why there is no
     * `put` helper to use. No idempotency key: the operation is idempotent by METHOD -- a repeat
     * answers 204 even in a full conversation, which the server orders its checks specifically to
     * guarantee -- so a key would buy nothing and a retry is already safe.
     */
    suspend fun pinMessage(chatId: ChatId, messageId: MessageId) {
        api.execute<Unit>(
            method = HttpMethod.Put,
            path = "conversations/${chatId.value}/pinned-messages/${messageId.value}",
            idempotencyKey = null,
        ) {}
    }

    /** `unpinMessage`, `204`. Unpinning something that is not pinned is a success. */
    suspend fun unpinMessage(chatId: ChatId, messageId: MessageId) {
        api.delete<Unit>("conversations/${chatId.value}/pinned-messages/${messageId.value}")
    }

    // -------------------------------------------------------------------------- reactions

    /**
     * Adds or removes a reaction to/from a message.
     *
     * Toggling is the expected behavior: reacting with an emoji you have already used removes it.
     */
    suspend fun react(messageId: MessageId, emoji: String) {
        val dto: MessageDto = api.post("messages/${messageId.value}/reactions") {
            setBody(ReactToMessageRequest(emoji = emoji))
        }
        withContext(ioDispatcher) {
            queries.transaction {
                cacheMessage(dto)
            }
        }
    }

    companion object {
        /** DECISIONS §5: "First history request returns the latest 30 messages." */
        const val PAGE_SIZE = 30

        /**
         * How many rows the screen observes BY DEFAULT. Larger than a page so the first screenful
         * and the run above it are already there, but bounded so a chat with 50k messages does not
         * load them all into memory to show the last screenful. `ChatViewModel` grows its own
         * window past this as the reader pages back -- see `ChatViewModel.loadOlder`.
         */
        const val TIMELINE_WINDOW = 200L

        /**
         * How many change pages one [reconcile] will walk.
         *
         * The cursor is stored per page, so stopping here loses nothing -- the next open resumes.
         * Four pages is 120 changes, which is far past what a conversation accumulates between
         * two openings and far short of what a long-dormant one could hand back at once.
         */
        const val MAX_RECONCILE_PAGES = 4
    }
}

// ------------------------------------------------------------------------------- the outbox
//
// The two rules the pending pass makes, lifted out of the loop they used to be written inside.
// Neither needs a database or a socket to be true, and both were wrong in a way that is invisible
// from outside the running app: a message that never goes out, and no sign that it did not.

/**
 * How many times the automatic pass will re-send a row before leaving it to the user.
 *
 * The number is a judgement call and is written down rather than left implicit. `markFailed` has
 * always incremented `retryCount` and NOTHING has ever read it, so a message the server
 * permanently refuses cost one request on every single chat open, forever. Three is enough to
 * carry a row through a transient outage and few enough that a poison row stops being free.
 */
const val MAX_AUTO_RETRIES = 3

/** A pending outbox row, reduced to exactly what a re-send needs. */
data class PendingSend(
    val chatId: String,
    val clientId: String,
    val idempotencyKey: String?,
    val text: String?,
    val replyToId: String?,
    val retryCount: Long,
)

/** What one pass did, by client id. Returned so the rule can be asserted without a database. */
data class PendingPassReport(
    val attempted: List<String>,
    val failed: List<String>,
    val skipped: List<String>,
)

/**
 * Whether the AUTOMATIC pass should still be paying for this row.
 *
 * A row with no stored idempotency key is not automatically retryable at all: re-sending without
 * the original key is how one message becomes two.
 */
fun isAutoRetryable(row: PendingSend, maxAutoRetries: Int = MAX_AUTO_RETRIES): Boolean =
    row.idempotencyKey != null && row.retryCount < maxAutoRetries

/**
 * Attempts every pending row, and lets each one fail on its own.
 *
 * THE DEFECT THIS REPLACES. The pass was `pending.forEach { deliver(...) }` with no per-item
 * guard, over a query with no chat filter, ordered oldest-first -- and `deliver` rethrows after
 * marking the row FAILED. So the first row the server permanently refused ended the pass and
 * every later pending message, in every conversation, was stranded behind it for good. Two
 * high-severity defects hid each other here: there was also no way to retry or delete the row
 * doing the blocking, so the state was not merely wrong, it was unreachable.
 *
 * [CancellationException] is rethrown FIRST and deliberately. A cancelled pass is the screen
 * closing, not a message failing, and swallowing it would both keep working inside a dead scope
 * and count the cancellation as a failure against the row.
 */
suspend fun deliverPendingPass(
    pending: List<PendingSend>,
    maxAutoRetries: Int = MAX_AUTO_RETRIES,
    deliver: suspend (PendingSend) -> Unit,
): PendingPassReport {
    val attempted = mutableListOf<String>()
    val failed = mutableListOf<String>()
    val skipped = mutableListOf<String>()

    pending.forEach { row ->
        if (!isAutoRetryable(row, maxAutoRetries)) {
            skipped += row.clientId
            return@forEach
        }
        attempted += row.clientId
        try {
            deliver(row)
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: Throwable) {
            failed += row.clientId
        }
    }
    return PendingPassReport(attempted = attempted, failed = failed, skipped = skipped)
}

// ------------------------------------------------------------------------- reconciliation plan

/** Which scope a [MessageRepository.delete] asks for. The wire values are the contract's enum. */
enum class MessageDeleteScope(val wire: String) {
    /** Hides the message for this member only. Available to any member, with no time limit. */
    SELF("self"),

    /**
     * Withdraws it for everyone. Own messages only, and only within 48 hours -- the server
     * answers `403 message_delete_not_allowed` and `409 delete_window_expired` respectively, and
     * `Message.can_delete_for_everyone` is its own answer to whether it may be offered.
     */
    EVERYONE("everyone"),
}

/** One page of the change feed, reduced to what the cache has to be told. */
data class ReconcilePlan(
    val updated: List<MessageDto>,
    val deleted: List<String>,
    val changeCursor: String,
    val hasMore: Boolean,
)

/**
 * Turns a change page into cache writes.
 *
 * Pure, and separated from the writing for the reason the outbox rules are: it is where the two
 * halves of the untagged `oneOf` are told apart, and getting it wrong is silent in both
 * directions -- a tombstone read as a message resurrects a deletion, a message read as a
 * tombstone erases one that is still there.
 *
 * CHANGES FOR ANOTHER CONVERSATION ARE DROPPED. The feed is per-conversation so this should never
 * fire, but `applyTombstone` matches on the server id ALONE, with no chat filter -- so a stray
 * row would blank a message in a conversation the caller was not even reading.
 */
fun planReconcile(page: MessageChangePage, chatId: ChatId): ReconcilePlan {
    val updated = mutableListOf<MessageDto>()
    val deleted = mutableListOf<String>()

    page.items.forEach { change ->
        when (change) {
            is MessageChange.Updated ->
                if (change.message.conversationId.equals(chatId.value, ignoreCase = true)) {
                    updated += change.message
                }
            is MessageChange.Deleted ->
                if (change.tombstone.conversationId.equals(chatId.value, ignoreCase = true)) {
                    deleted += change.tombstone.id
                }
        }
    }

    return ReconcilePlan(
        updated = updated,
        deleted = deleted,
        changeCursor = page.changeCursor,
        // `next_cursor` is only honoured together with `has_more`, exactly as the feed reader
        // does: the server pairs them, and a "more to fetch" with nothing to fetch would spin.
        hasMore = page.pageInfo.hasMore,
    )
}
